import { createContext, useContext, useEffect, useState } from 'react'

export type ShellOverlayMetrics = {
  bannerHeightPx: number
  keyboardInsetPx: number
  setBannerHeightPx: (px: number) => void
}

export const defaultShellOverlayMetrics: ShellOverlayMetrics = {
  bannerHeightPx: 0,
  keyboardInsetPx: 0,
  setBannerHeightPx: () => undefined,
}

export const ShellOverlayMetricsContext = createContext<ShellOverlayMetrics>(defaultShellOverlayMetrics)

const FOCUSABLE_FIELD_SELECTOR = 'input, textarea, select, [contenteditable="true"]'

/** Best-effort iOS keyboard overlap from the visual viewport (0 when unsupported). */
export function useVisualViewportKeyboardInset(): number {
  const [inset, setInset] = useState(0)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) {
      return undefined
    }

    const update = () => {
      const overlap = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
      setInset(overlap)
    }

    const onFocusIn = (event: FocusEvent) => {
      update()
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      if (!target.matches(FOCUSABLE_FIELD_SELECTOR)) return
      window.setTimeout(() => {
        target.scrollIntoView({ block: 'center', inline: 'nearest' })
      }, 50)
    }

    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    window.addEventListener('focusin', onFocusIn)
    update()
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      window.removeEventListener('focusin', onFocusIn)
    }
  }, [])

  return inset
}

export function useShellOverlayMetrics(): ShellOverlayMetrics {
  return useContext(ShellOverlayMetricsContext)
}
